import { ApiError } from "../../data/api";
import { date, initials, monthLabel } from "../../lib/format";

/**
 * Instagram-Insights, die eine Agentur an eine Marke freigibt (13_oeffentlich/insights.html; OE-030, OE-031).
 * Reine Funktionen über der Antwort von ig_share_open (0023). Regeln aus dem bisherigen Portal (insights-view.js):
 * nur freigegebene Blöcke; „kein Wert“ bleibt leer und wird nie zu 0; erreichte Konten heißen nie Aufrufe; Anteile werden
 * nie selbst gerechnet; fremde Adressen nur als Link zu Instagram, nie als Bild; alle Texte nur als Text.
 */

export interface ShareProfile {
  username: string;
  name: string | null;
  followers: number | null;
  media_count: number | null;
  /** 0164: Pfad des Profilbilds im Bucket ig-media */
  picture?: string | null;
}
export interface SharePost {
  permalink: string | null;
  type: string | null;
  posted_at: string | null;
  metrics: Record<string, unknown> | null;
  /** 0164: Pfad des Vorschaubilds im Bucket ig-media */
  thumb?: string | null;
}
export interface ShareResponse {
  ok: boolean;
  remaining?: number;
  blocks?: string[];
  period?: string;
  profile?: ShareProfile | null;
  period_totals?: Record<string, unknown> | null;
  audience?: Record<string, unknown> | null;
  monthly?: { month: string; metrics: Record<string, unknown> | null }[] | null;
  top_posts?: SharePost[] | null;
  /** seit 0034: Agentur, Name des Creators, Ablauf und Stand der Zahlen */
  agency?: string | null;
  talent_name?: string | null;
  expires_at?: string | null;
  as_of?: string | null;
  /** Tageswerte (nur die eigene Ansicht von Creator und Agentur; die Marke bekommt keine) für die Verlaufskurve */
  daily?: { day: string; metrics: Record<string, unknown> | null }[] | null;
  /** 0340: Vergleich zum Vorzeitraum je Kennzahl (nur aus vollständigen Tageswerten; app.ig_compare bzw. compareFromDaily) */
  compare?: Record<string, unknown> | null;
  /** 0340: Storys einzeln (Block story_items) */
  stories?: ShareStory[] | null;
  /** 0340: Zeitfenster, das Instagram für die Zielgruppe geliefert hat (last_90_days, last_30_days, this_month) */
  audience_timeframe?: string | null;
  /** 0429: signierte Adressen (eine Stunde) der Bilder dieser Antwort, Pfad → Adresse; liefert die Serverfunktion ig-share */
  media_urls?: Record<string, string> | null;
}
export interface ShareStory {
  posted_at: string | null;
  type?: string | null;
  metrics: Record<string, unknown> | null;
  measured_at?: string | null;
  measurements?: number | null;
  few_viewers?: boolean | null;
}

/** 0164: Pfad eines Bilds im Bucket ig-media (<agentur>/<creator>/<datei>), wie die Formatprüfung der Datenbank */
export const IG_MEDIA_PATH = /^[0-9a-f-]{36}\/[0-9a-f-]{36}\/[0-9a-f-]{36}\.(jpg|png|webp)$/;
/** Pfad im Bucket ig-media → anzeigbare Adresse (oder null: dann Platzhalter) */
export type MediaUrlResolver = (path: unknown) => string | null;

/**
 * 0429: Der Bucket ig-media ist privat. Angezeigt wird nur eine kurzlebige signierte Adresse des eigenen Speichers für genau diesen
 * Pfad (aus der Serverfunktion ig-share bzw. aus createSignedUrls mit der Anmeldung). Alles andere, auch eine öffentliche Adresse
 * oder eine fremde Bildadresse, ergibt null; fremde Adressen erscheinen nie als Bild.
 */
export function signedIgMediaUrl(urls: Readonly<Record<string, string>> | null | undefined, path: unknown): string | null {
  if (typeof path !== "string" || !IG_MEDIA_PATH.test(path) || !urls || typeof urls !== "object") return null;
  const raw = Object.prototype.hasOwnProperty.call(urls, path) ? urls[path] : undefined;
  if (typeof raw !== "string" || raw.length > 2000) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  const local = u.protocol === "http:" && (u.hostname === "localhost" || u.hostname === "127.0.0.1");
  if ((u.protocol !== "https:" && !local) || u.username || u.password) return null;
  // Pfade bestehen nur aus Hex-Ziffern, Bindestrichen, „/“ und „.“; eine kodierte Schreibweise gilt nicht
  if (u.pathname !== `/storage/v1/object/sign/ig-media/${path}` || !u.searchParams.get("token")) return null;
  return u.toString();
}
export function igMediaResolver(urls: Readonly<Record<string, string>> | null | undefined): MediaUrlResolver {
  return (path) => signedIgMediaUrl(urls, path);
}
/** Bildpfade, die eine Ansicht zeigt (Profilbild beim Block profile, Vorschaubilder der Top-Beiträge), ohne Doppelte, sortiert */
export function igMediaPaths(r: Pick<ShareResponse, "blocks" | "profile" | "top_posts"> | null | undefined): string[] {
  if (!r) return [];
  const blocks = Array.isArray(r.blocks) ? r.blocks : [];
  const out = new Set<string>();
  const pic = r.profile?.picture;
  if (blocks.includes("profile") && typeof pic === "string" && IG_MEDIA_PATH.test(pic)) out.add(pic);
  if (blocks.includes("top_posts") && Array.isArray(r.top_posts)) {
    for (const p of r.top_posts) if (p && typeof p.thumb === "string" && IG_MEDIA_PATH.test(p.thumb)) out.add(p.thumb);
  }
  return [...out].sort();
}

/** Sprache der Seite für die Marke (SICHT-OEFFENTLICH-BETRIEB-04): Deutsch oder Englisch, Standard Deutsch. */
export type InsightsLang = "de" | "en";
const en = (lang: InsightsLang) => lang === "en";

/** Blöcke wie app.ig_blocks() (0023), benannt für die Marke (ehrliche Begriffe wie im bisherigen Portal). */
export const BLOCK_LABEL: Record<string, string> = {
  profile: "Profilkopf",
  age: "Altersgruppen",
  gender: "Geschlecht",
  countries: "Länder",
  cities: "Städte",
  reach: "Erreichte Konten",
  views: "Aufrufe",
  interactions: "Interaktionen",
  stories: "Story-Aufrufe",
  content_types: "Reels und Beiträge im Vergleich",
  follower_split: "Follower und Nicht-Follower",
  profile_clicks: "Klicks im Profil",
  top_posts: "Top-Beiträge",
  story_items: "Storys einzeln",
};
const BLOCK_LABEL_EN: Record<string, string> = {
  profile: "Profile header", age: "Age groups", gender: "Gender", countries: "Countries", cities: "Cities", reach: "Accounts reached",
  views: "Views", interactions: "Interactions", stories: "Story views", content_types: "Reels and posts compared",
  follower_split: "Followers and non-followers", profile_clicks: "Profile taps", top_posts: "Top posts",
  story_items: "Individual stories",
};
export const BLOCK_ORDER = Object.keys(BLOCK_LABEL);
export function blockLabel(key: string, lang: InsightsLang = "de"): string {
  return (en(lang) ? BLOCK_LABEL_EN[key] : BLOCK_LABEL[key]) ?? key;
}

const PERIOD: Record<string, { label: string; lower: string }> = {
  "7d": { label: "Letzte 7 Tage", lower: "letzte 7 Tage" },
  "30d": { label: "Letzte 30 Tage", lower: "letzte 30 Tage" },
  "90d": { label: "Letzte 90 Tage", lower: "letzte 90 Tage" },
  "12m": { label: "Letzte 12 Monate", lower: "letzte 12 Monate" },
  all: { label: "Gesamter Verlauf", lower: "gesamter Verlauf" },
};
const PERIOD_EN: Record<string, { label: string; lower: string }> = {
  "7d": { label: "Last 7 days", lower: "last 7 days" },
  "30d": { label: "Last 30 days", lower: "last 30 days" },
  "90d": { label: "Last 90 days", lower: "last 90 days" },
  "12m": { label: "Last 12 months", lower: "last 12 months" },
  all: { label: "Entire history", lower: "entire history" },
};
const periodOf = (lang: InsightsLang) => (en(lang) ? PERIOD_EN : PERIOD);
export function periodLabel(key: string, lang: InsightsLang = "de"): string {
  return periodOf(lang)[key]?.label ?? key;
}
/** Zeitraumwerte liefert Instagram höchstens für 90 Tage; für 12 Monate und den Gesamtverlauf gelten dort die 90-Tage-Werte. */
export function totalsPeriod(key: string): string {
  return key === "12m" || key === "all" ? "90d" : key;
}

// ── Zahlen ─────────────────────────────────────────────────────────────────────────────────────────────

/** Nur echte Zahlen; alles andere (auch null) ist „kein Wert“ und wird nie zu 0. */
export function num(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v);
  return null;
}
const locale = (lang: InsightsLang) => (en(lang) ? "en-GB" : "de-DE");
export function countText(n: number, lang: InsightsLang = "de"): string {
  return new Intl.NumberFormat(locale(lang), { maximumFractionDigits: 0 }).format(Math.round(n));
}
/** 0.295 → „29,5 %“ (geschütztes Leerzeichen, damit das Zeichen nie allein umbricht); englisch „29.5%“. */
export function shareText(fraction: number, lang: InsightsLang = "de"): string {
  const n = new Intl.NumberFormat(locale(lang), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(fraction * 100);
  return en(lang) ? `${n}%` : `${n} %`;
}
/**
 * Formatierer für eine Reihe von Zielgruppenwerten: sind alle Werte zwischen 0 und 1, sind es Anteile (so liefert die Datenbank
 * Zielgruppen) und erscheinen als Prozent, sonst als Anzahl. Dieselbe Regel gilt für die Marke und das Creator-Portal (SICHT-CREATOR-05).
 */
export function audienceFormatter(values: readonly number[], lang: InsightsLang = "de"): (v: number) => string {
  return allFractions([...values]) ? (v) => shareText(v, lang) : (v) => countText(v, lang);
}

// ── Kacheln ────────────────────────────────────────────────────────────────────────────────────────────

export interface Tile {
  key: string;
  label: string;
  value: number;
  display: string;
  sub: string;
  /** 0340: Veränderung zum gleich langen Zeitraum davor (nur aus vollständigen Tageswerten), sonst fehlt sie */
  compare?: { text: string; dir: "up" | "down" | "flat"; sr: string; basis?: string } | null;
}

// ── Vergleich zum Vorzeitraum (0340) ────────────────────────────────────────────────────────────────────

/** Summierbare Tageswerte; erreichte Konten zählen Menschen und lassen sich nicht aus Tagen summieren */
export const COMPARE_KEYS = ["views", "interactions", "profile_clicks"] as const;
export interface CompareValue { current: number; previous: number; days: number }

function dayShift(day: string, n: number): string {
  const d = new Date(`${day.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
/**
 * Gleiche Regel wie app.ig_compare (0340): Stand asOf deckt die Tage asOf − n … asOf − 1 ab, der Vorzeitraum die n Tage davor.
 * Nur 7 und 30 Tage (90 Tage: Instagram hält Tageswerte nur 90 Tage, der Vorzeitraum fehlt immer) und nur, wenn JEDER Tag beider
 * Zeiträume einen Wert hat; nichts wird hochgerechnet.
 */
export function compareFromDaily(daily: ShareResponse["daily"], period: string, asOf: string | null | undefined, keys: readonly string[]): Record<string, CompareValue> | null {
  const n = period === "7d" ? 7 : period === "30d" ? 30 : null;
  if (!n || !asOf || !Array.isArray(daily)) return null;
  const byDay = new Map<string, Record<string, unknown>>();
  for (const d of daily) if (d && typeof d.day === "string" && isRecord(d.metrics)) byDay.set(d.day.slice(0, 10), d.metrics);
  const out: Record<string, CompareValue> = {};
  for (const k of COMPARE_KEYS) {
    if (!keys.includes(k)) continue;
    let cur = 0, prev = 0, ok = true;
    for (let i = 1; i <= 2 * n && ok; i++) {
      const v = num(byDay.get(dayShift(asOf, -i))?.[k]);
      if (v === null) ok = false;
      else if (i <= n) cur += v;
      else prev += v;
    }
    if (ok) out[k] = { current: cur, previous: prev, days: n };
  }
  return Object.keys(out).length ? out : null;
}
/**
 * „+12 % zu den 30 Tagen davor“; ohne Vorzeitraumwert (0) kein Prozentwert.
 * K-443: Die Kachel zeigt den Zeitraumwert von Meta, der Vergleich rechnet mit der Summe der Tageswerte. Weichen beide um mehr als
 * 2 % voneinander ab (Nachlieferungen und Korrekturen bei Meta), fehlt der Vergleich, damit die Prozentangabe der Zahl auf der
 * Kachel nie widerspricht; der Hinweis für Screenreader und Mauszeiger nennt beide Tagessummen.
 */
export const COMPARE_MAX_DRIFT = 0.02;
export function compareText(c: unknown, lang: InsightsLang = "de", tileValue?: number | null): Tile["compare"] {
  if (!isRecord(c)) return null;
  const cur = num(c.current), prev = num(c.previous), days = num(c.days);
  if (cur === null || prev === null || !days || prev <= 0) return null;
  if (typeof tileValue === "number" && Math.abs(tileValue - cur) > COMPARE_MAX_DRIFT * Math.max(Math.abs(tileValue), Math.abs(cur), 1)) return null;
  const pct = Math.round(((cur - prev) / prev) * 100);
  const n = new Intl.NumberFormat(locale(lang), { maximumFractionDigits: 0, signDisplay: "exceptZero" }).format(pct).replace("-", "−");
  const shown = pct === 0 ? (en(lang) ? "±0%" : "±0\u00a0%") : en(lang) ? `${n}%` : `${n}\u00a0%`;
  const dir = pct > 0 ? "up" : pct < 0 ? "down" : "flat";
  const basis = en(lang)
    ? `Sum of daily values ${countText(cur, lang)} · previous ${days} days ${countText(prev, lang)}`
    : `Summe der Tageswerte ${countText(cur, lang)} · ${days} Tage davor ${countText(prev, lang)}`;
  return en(lang)
    ? { text: `${shown} vs. the previous ${days} days`, dir, sr: `${shown} compared with the previous ${days} days (${basis})`, basis }
    : { text: `${shown} zu den ${days} Tagen davor`, dir, sr: `${shown} im Vergleich zu den ${days} Tagen davor (${basis})`, basis };
}

// ── Storys einzeln (0340) ──────────────────────────────────────────────────────────────────────────────

const STORY_COLS: readonly { key: string; label: string; en: string }[] = [
  { key: "reach", label: "Erreichte Konten", en: "Accounts reached" },
  { key: "views", label: "Aufrufe", en: "Views" },
  { key: "tap_forward", label: "Weiter getippt", en: "Tapped forward" },
  { key: "tap_back", label: "Zurück getippt", en: "Tapped back" },
  { key: "swipe_forward", label: "Zum nächsten Konto gewischt", en: "Swiped to next account" },
  { key: "tap_exit", label: "Verlassen", en: "Exited" },
  { key: "shares", label: "Geteilt", en: "Shared" },
  { key: "profile_visits", label: "Profilaufrufe", en: "Profile visits" },
  { key: "follows", label: "Neue Follower", en: "New followers" },
];
export interface StoriesView {
  columns: { key: string; label: string }[];
  rows: { key: string; when: string; type: string; live: boolean; few: boolean; measured: string | null; values: (number | null)[] }[];
  /** Durchschnittliche Reichweite je gemessener Story (nur Storys mit Wert) */
  avgReach: { value: number; display: string; count: number } | null;
}
export function storiesView(stories: ShareResponse["stories"], lang: InsightsLang = "de", nowMs: number = Date.now()): StoriesView | null {
  if (!Array.isArray(stories) || !stories.length) return null;
  const list = stories.filter((s) => s && typeof s.posted_at === "string").sort((a, b) => b.posted_at!.localeCompare(a.posted_at!));
  if (!list.length) return null;
  const read = (s: ShareStory, key: string): number | null => {
    const m = isRecord(s.metrics) ? s.metrics : {};
    if (key in m) return num(m[key]);
    const bd = isRecord(m.navigation_breakdown) ? m.navigation_breakdown : {};
    return num(bd[key]);
  };
  const columns = STORY_COLS.filter((c) => list.some((s) => read(s, c.key) !== null));
  const fmt = new Intl.DateTimeFormat(locale(lang), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  const rows = list.map((s, i) => {
    const posted = Date.parse(s.posted_at!);
    const measured = typeof s.measured_at === "string" ? Date.parse(s.measured_at) : NaN;
    const hours = Number.isFinite(measured) ? Math.max(0, Math.round((measured - posted) / 3_600_000)) : null;
    const times = num(s.measurements) ?? 0;
    const live = nowMs - posted < 24 * 3_600_000;
    const measuredText = hours === null ? null : en(lang)
      ? `last measured ${hours} h after posting${times > 1 ? ` · ${times} measurements` : ""}`
      : `zuletzt gemessen ${hours} Std. nach Veröffentlichung${times > 1 ? ` · ${times} Messungen` : ""}`;
    const kind = String(s.type ?? "").toUpperCase();
    const type = kind === "VIDEO" ? "Video" : kind === "IMAGE" ? (en(lang) ? "Image" : "Bild") : "Story";
    return { key: `${i}-${s.posted_at}`, when: fmt.format(new Date(posted)), type, live, few: s.few_viewers === true && columns.every((c) => read(s, c.key) === null),
      measured: measuredText, values: columns.map((c) => read(s, c.key)) };
  });
  const reaches = list.map((s) => read(s, "reach")).filter((v): v is number => v !== null);
  const avg = reaches.length ? reaches.reduce((a, b) => a + b, 0) / reaches.length : null;
  return {
    columns: columns.map((c) => ({ key: c.key, label: en(lang) ? c.en : c.label })), rows,
    avgReach: avg === null ? null : { value: avg, display: countText(avg, lang), count: reaches.length },
  };
}

// ── Zeitfenster der Zielgruppe (0340) ──────────────────────────────────────────────────────────────────

const TIMEFRAME: Record<string, { de: string; en: string }> = {
  last_90_days: { de: "letzte 90 Tage", en: "last 90 days" }, last_30_days: { de: "letzte 30 Tage", en: "last 30 days" },
  last_14_days: { de: "letzte 14 Tage", en: "last 14 days" }, this_month: { de: "laufender Monat", en: "current month" },
  this_week: { de: "laufende Woche", en: "current week" }, prev_month: { de: "Vormonat", en: "previous month" },
};
/** Unterzeile des Reiters „Zielgruppe“: Zeitfenster von Instagram; folgt der Zeitwahl nicht */
export function audienceNote(timeframe: string | null | undefined, lang: InsightsLang = "de"): string {
  const tf = timeframe ? TIMEFRAME[timeframe] : undefined;
  if (en(lang)) return tf ? `Instagram time window: ${tf.en} · does not follow the period selection` : "Time window set by Instagram (up to 90 days) · does not follow the period selection";
  return tf ? `Instagram-Zeitfenster: ${tf.de} · folgt der Zeitwahl nicht` : "Zeitfenster von Instagram (bis zu 90 Tage) · folgt der Zeitwahl nicht";
}
const TILE_KEYS = ["reach", "views", "interactions", "stories", "profile_clicks", "follower_split"] as const;

function tileSub(key: string, periodKey: string, lang: InsightsLang = "de"): string {
  const lower = periodOf(lang)[totalsPeriod(periodKey)]?.lower ?? periodKey;
  if (en(lang)) {
    if (key === "reach") return `Period: ${lower}`;
    if (key === "profile_clicks") return `Taps on the buttons in the profile · ${lower}`;
    if (key === "follower_split") return `Share of followers among accounts reached · ${lower}`;
    return periodKey === "12m" || periodKey === "all" ? `Period: ${lower}` : "In the selected period";
  }
  if (key === "reach") return `Zeitraum: ${lower}`;
  if (key === "profile_clicks") return `Tipps auf die Schaltflächen im Profil · ${lower}`;
  if (key === "follower_split") return `Anteil der Follower an den erreichten Konten · ${lower}`;
  return periodKey === "12m" || periodKey === "all" ? `Zeitraum: ${lower}` : "Im ausgewählten Zeitraum";
}

// ── Aufschlüsselungen (Formate, Follower-Anteil als Objekt) ─────────────────────────────────────────────

const PART_LABEL: Record<string, string> = {
  reels: "Reels", reel: "Reels", posts: "Beiträge", post: "Beiträge", feed: "Beiträge", image: "Bilder", images: "Bilder",
  carousel: "Karussells", carousels: "Karussells", carousel_album: "Karussells", stories: "Storys", story: "Storys", video: "Videos", videos: "Videos",
  followers: "Follower", follower: "Follower", non_followers: "Nicht-Follower", non_follower: "Nicht-Follower",
  views: "Aufrufe", reach: "Erreichte Konten", interactions: "Interaktionen", likes: "Likes", comments: "Kommentare", saved: "Gespeichert",
  saves: "Gespeichert", shares: "Geteilt", replies: "Antworten",
};
const PART_LABEL_EN: Record<string, string> = {
  reels: "Reels", reel: "Reels", posts: "Posts", post: "Posts", feed: "Posts", image: "Images", images: "Images",
  carousel: "Carousels", carousels: "Carousels", carousel_album: "Carousels", stories: "Stories", story: "Stories", video: "Videos", videos: "Videos",
  followers: "Followers", follower: "Followers", non_followers: "Non-followers", non_follower: "Non-followers",
  views: "Views", reach: "Accounts reached", interactions: "Interactions", likes: "Likes", comments: "Comments", saved: "Saved",
  saves: "Saved", shares: "Shared", replies: "Replies",
};
export function partLabel(key: string, lang: InsightsLang = "de"): string {
  const k = key.trim().toLowerCase();
  const table = en(lang) ? PART_LABEL_EN : PART_LABEL;
  if (table[k]) return table[k]!;
  const plain = key.replace(/_/g, " ").trim();
  return plain ? plain[0]!.toUpperCase() + plain.slice(1) : key;
}

export interface Breakdown {
  key: string;
  title: string;
  /** Liste: Teil → Zahl; Tabelle: Teil → Kennzahl → Zahl */
  list: { key: string; label: string; value: number; display: string }[] | null;
  table: { columns: { key: string; label: string }[]; rows: { key: string; label: string; values: (number | null)[] }[] } | null;
  kind: "count" | "share";
}
function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}
/** Zahlen zwischen 0 und 1 sind Anteile (so liefert die Datenbank Zielgruppen), sonst Anzahlen. */
function allFractions(values: number[]): boolean {
  return values.length > 0 && values.every((v) => v >= 0 && v <= 1);
}
function breakdownOf(key: string, raw: Record<string, unknown>, lang: InsightsLang = "de"): Breakdown | null {
  const entries = Object.entries(raw);
  const numbers = entries.map(([k, v]) => [k, num(v)] as const).filter((e): e is readonly [string, number] => e[1] !== null);
  if (numbers.length && numbers.length === entries.filter(([, v]) => !isRecord(v)).length) {
    const kind = allFractions(numbers.map((e) => e[1])) ? "share" : "count";
    return {
      key, title: blockLabel(key, lang), kind, table: null,
      list: numbers.map(([k, v]) => ({ key: k, label: partLabel(k, lang), value: v, display: kind === "share" ? shareText(v, lang) : countText(v, lang) })).sort((a, b) => b.value - a.value),
    };
  }
  const nested = entries.filter((e): e is [string, Record<string, unknown>] => isRecord(e[1]));
  if (!nested.length) return null;
  const colKeys = [...new Set(nested.flatMap(([, v]) => Object.keys(v).filter((k) => num(v[k]) !== null)))];
  if (!colKeys.length) return null;
  return {
    key, title: blockLabel(key, lang), kind: "count", list: null,
    table: {
      columns: colKeys.map((k) => ({ key: k, label: partLabel(k, lang) })),
      rows: nested.map(([k, v]) => ({ key: k, label: partLabel(k, lang), values: colKeys.map((c) => num(v[c])) })),
    },
  };
}

// ── Zielgruppe ─────────────────────────────────────────────────────────────────────────────────────────

const AUDIENCE: readonly { breakdown: string; block: string }[] = [
  { breakdown: "age", block: "age" },
  { breakdown: "gender", block: "gender" },
  { breakdown: "country", block: "countries" },
  { breakdown: "city", block: "cities" },
];
const AGE_ORDER = ["13–17", "18–24", "25–34", "35–44", "45–54", "55–64", "65+"];
const GENDER: Record<string, string> = { f: "Weiblich", female: "Weiblich", m: "Männlich", male: "Männlich", u: "Keine Angabe", unknown: "Keine Angabe" };
const GENDER_EN: Record<string, string> = { f: "Female", female: "Female", m: "Male", male: "Male", u: "Not specified", unknown: "Not specified" };
/** Regionen, die keine Länder sind, und historische Codes bleiben roh (bisheriges Portal, fs102). */
const NOT_A_COUNTRY = new Set(["ZZ", "QO", "QU", "EU", "EZ", "UN", "XA", "XB", "XK", "DD", "SU", "CS", "YU", "AN", "FX", "ZR", "TP", "BU"]);
const regionNames: Partial<Record<InsightsLang, Intl.DisplayNames | null>> = {};
function countryName(code: string, lang: InsightsLang = "de"): string {
  const c = code.trim();
  if (!/^[A-Z]{2}$/.test(c) || NOT_A_COUNTRY.has(c)) return c;
  if (regionNames[lang] === undefined) {
    try {
      regionNames[lang] = new Intl.DisplayNames([lang], { type: "region" });
    } catch {
      regionNames[lang] = null;
    }
  }
  try {
    const n = regionNames[lang]?.of(c);
    return n && n !== c ? n : c;
  } catch {
    return c;
  }
}
export function audienceLabel(breakdown: string, key: string, lang: InsightsLang = "de"): string {
  if (breakdown === "age") return key.replace(/-/g, "–");
  if (breakdown === "gender") return (en(lang) ? GENDER_EN : GENDER)[key.trim().toLowerCase()] ?? key;
  if (breakdown === "country") return countryName(key, lang);
  return key;
}

export interface AudienceBlock {
  block: string;
  title: string;
  kind: "share" | "count";
  entries: { key: string; label: string; value: number; display: string }[];
}
export function audienceBlocks(audience: Record<string, unknown> | null | undefined, blocks: readonly string[], lang: InsightsLang = "de"): AudienceBlock[] {
  if (!isRecord(audience)) return [];
  const out: AudienceBlock[] = [];
  for (const a of AUDIENCE) {
    if (!blocks.includes(a.block)) continue;
    const data = audience[a.breakdown];
    if (!isRecord(data)) continue;
    const pairs = Object.entries(data).map(([k, v]) => [k, num(v)] as const).filter((e): e is readonly [string, number] => e[1] !== null);
    if (!pairs.length) continue;
    const kind = allFractions(pairs.map((p) => p[1])) ? "share" : "count";
    let entries = pairs.map(([k, v]) => ({ key: k, label: audienceLabel(a.breakdown, k, lang), value: v, display: kind === "share" ? shareText(v, lang) : countText(v, lang) }));
    if (a.breakdown === "age") {
      const rank = (l: string) => (AGE_ORDER.indexOf(l) === -1 ? 99 : AGE_ORDER.indexOf(l));
      entries = entries.sort((x, y) => rank(x.label) - rank(y.label) || x.label.localeCompare(y.label, lang));
    } else {
      entries = entries.sort((x, y) => y.value - x.value).slice(0, 10);
    }
    out.push({ block: a.block, title: blockLabel(a.block, lang), kind, entries });
  }
  return out;
}

// ── Monatswerte (Zeitraum 12 Monate oder gesamter Verlauf) ──────────────────────────────────────────────

/** Spalten der Monatswerte und der Block, der sie freigibt. Die Datenbank schickt sie ungefiltert; hier gilt die Freigabe. */
const MONTHLY: readonly { key: string; block: string; label: string; en: string }[] = [
  { key: "views", block: "views", label: "Aufrufe", en: "Views" },
  { key: "interactions", block: "interactions", label: "Interaktionen", en: "Interactions" },
  { key: "reach", block: "reach", label: "Erreichte Konten", en: "Accounts reached" },
  { key: "followers_end", block: "profile", label: "Follower am Monatsende", en: "Followers at month end" },
];
function monthText(month: string, lang: InsightsLang): string {
  if (!en(lang)) return monthLabel(month);
  const [y, m] = month.split("-").map(Number);
  return y && m ? new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, 1))) : month;
}
export interface MonthlyView {
  columns: { key: string; label: string }[];
  rows: { month: string; label: string; values: (number | null)[] }[];
}
export function monthlyView(monthly: ShareResponse["monthly"], blocks: readonly string[], lang: InsightsLang = "de"): MonthlyView | null {
  if (!Array.isArray(monthly) || !monthly.length) return null;
  const rows0 = monthly
    .filter((m) => m && typeof m.month === "string")
    .map((m) => ({ month: m.month.slice(0, 7), metrics: isRecord(m.metrics) ? m.metrics : {} }))
    .sort((a, b) => a.month.localeCompare(b.month));
  const columns = MONTHLY.filter((c) => blocks.includes(c.block) && rows0.some((r) => num(r.metrics[c.key]) !== null));
  if (!columns.length) return null;
  return {
    columns: columns.map((c) => ({ key: c.key, label: en(lang) ? c.en : c.label })),
    rows: rows0.map((r) => ({ month: r.month, label: monthText(r.month, lang), values: columns.map((c) => num(r.metrics[c.key])) })),
  };
}

// ── Top-Beiträge ───────────────────────────────────────────────────────────────────────────────────────

const POST_METRICS: readonly { key: string; label: string; en: string; aliases: string[] }[] = [
  { key: "views", label: "Aufrufe", en: "Views", aliases: ["views", "plays", "video_views"] },
  { key: "reach", label: "Erreichte Konten", en: "Accounts reached", aliases: ["reach"] },
  { key: "likes", label: "Likes", en: "Likes", aliases: ["likes", "like_count"] },
  { key: "comments", label: "Kommentare", en: "Comments", aliases: ["comments", "comments_count"] },
  { key: "saved", label: "Gespeichert", en: "Saved", aliases: ["saved", "saves"] },
  { key: "shares", label: "Geteilt", en: "Shared", aliases: ["shares"] },
];
export function postTypeLabel(type: string | null | undefined, lang: InsightsLang = "de"): string {
  const t = String(type ?? "").toUpperCase();
  if (t.includes("REEL") || t === "VIDEO") return "Reel";
  if (t.includes("CAROUSEL")) return en(lang) ? "Carousel" : "Karussell";
  if (t.includes("STORY")) return "Story";
  if (t === "IMAGE") return en(lang) ? "Image" : "Bild";
  return en(lang) ? "Post" : "Beitrag";
}
/** Nur Links zu Instagram über https; alles andere wird nicht verlinkt (die Seite steht anonym im Netz). */
export function safeInstagramUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const u = new URL(value.trim());
    if (u.protocol !== "https:" || !/^(www\.)?instagram\.com$/i.test(u.hostname) || u.username || u.password) return null;
    return u.toString();
  } catch {
    return null;
  }
}
export type PostKind = "reel" | "carousel" | "story" | "image" | "post";
export function postKind(type: string | null | undefined): PostKind {
  const t = String(type ?? "").toUpperCase();
  if (t.includes("REEL") || t === "VIDEO") return "reel";
  if (t.includes("CAROUSEL")) return "carousel";
  if (t.includes("STORY")) return "story";
  if (t === "IMAGE") return "image";
  return "post";
}
export interface PostsView {
  columns: { key: string; label: string }[];
  rows: { key: string; type: string; kind: PostKind; date: string | null; url: string | null; thumb: string | null; views: number | null; values: (number | null)[] }[];
}
export function postsView(posts: ShareResponse["top_posts"], lang: InsightsLang = "de", mediaUrl: MediaUrlResolver = () => null): PostsView | null {
  if (!Array.isArray(posts) || !posts.length) return null;
  const read = (p: SharePost, aliases: string[]) => {
    const m = isRecord(p.metrics) ? p.metrics : {};
    for (const a of aliases) {
      const v = num(m[a]);
      if (v !== null) return v;
    }
    return null;
  };
  const list = posts.filter((p) => p && typeof p === "object");
  const columns = POST_METRICS.filter((c) => list.some((p) => read(p, c.aliases) !== null));
  const rows = list
    .map((p, i) => ({
      key: `${i}-${p.permalink ?? ""}`,
      type: postTypeLabel(p.type, lang),
      kind: postKind(p.type),
      date: p.posted_at ? date(p.posted_at, "UTC", locale(lang)) : null,
      url: safeInstagramUrl(p.permalink),
      thumb: mediaUrl(p.thumb),
      values: columns.map((c) => read(p, c.aliases)),
      views: read(p, ["views", "plays", "video_views"]),
    }))
    .sort((a, b) => (b.views ?? -1) - (a.views ?? -1));
  if (!rows.length) return null;
  return { columns: columns.map((c) => ({ key: c.key, label: en(lang) ? c.en : c.label })), rows };
}

// ── Gesamte Ansicht ────────────────────────────────────────────────────────────────────────────────────

export interface InsightsView {
  email: string;
  agency: string | null;
  expiresAt: string | null;
  asOf: string | null;
  blocks: string[];
  period: string;
  periodLabel: string;
  title: string;
  /** Kürzel im Kreis rechts oben, nur mit freigegebenem Profilkopf */
  initials: string | null;
  /** 0164: Benutzername und Profilbild (öffentliche Adresse), nur mit freigegebenem Profilkopf */
  username: string | null;
  picture: string | null;
  /** Verlauf der Tageswerte (nur eigene Ansicht): Kennzahl und Punkte je Tag */
  trend: { key: string; label: string; points: { day: string; value: number }[] } | null;
  subline: string;
  profileTiles: Tile[];
  tiles: Tile[];
  breakdowns: Breakdown[];
  audience: AudienceBlock[];
  monthly: MonthlyView | null;
  posts: PostsView | null;
  /** 0340: Storys einzeln */
  stories: StoriesView | null;
  /** 0340: Unterzeile des Reiters „Zielgruppe“ */
  audienceNote: string;
  sharedLabels: string[];
  missingLabels: string[];
  empty: boolean;
  /** Sprache, in der die Ansicht gebaut ist */
  lang: InsightsLang;
}

/** Tagesverlauf für die Kurve: Aufrufe, sonst erreichte Konten; nur Tage mit Wert, höchstens die Tage des Zeitraums */
function trendOf(r: ShareResponse, has: (b: string) => boolean, period: string, lang: InsightsLang): InsightsView["trend"] {
  if (!Array.isArray(r.daily) || !r.daily.length) return null;
  const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
  const rows = r.daily.filter((d) => d && typeof d.day === "string").sort((a, b) => a.day.localeCompare(b.day)).slice(-days);
  for (const key of ["views", "reach"]) {
    if (!has(key)) continue;
    const points = rows.map((d) => ({ day: d.day.slice(0, 10), value: num(isRecord(d.metrics) ? d.metrics[key] : null) })).filter((x): x is { day: string; value: number } => x.value !== null);
    if (points.length >= 2) return { key, label: blockLabel(key, lang), points };
  }
  return null;
}

/** mediaUrl: Standard sind die signierten Adressen der Antwort selbst (media_urls, Freigabe an die Marke) */
export function buildView(r: ShareResponse, email: string, lang: InsightsLang = "de", mediaUrl: MediaUrlResolver = igMediaResolver(r.media_urls), nowMs: number = Date.now()): InsightsView {
  const blocks = (Array.isArray(r.blocks) ? r.blocks : []).filter((b) => typeof b === "string");
  const has = (b: string) => blocks.includes(b);
  const period = typeof r.period === "string" ? r.period : "30d";
  const profile = has("profile") && r.profile && typeof r.profile.username === "string" ? r.profile : null;
  const name = profile?.name?.trim() || (typeof r.talent_name === "string" && r.talent_name.trim() ? r.talent_name.trim() : null);
  const title = name ?? (profile ? `@${profile.username}` : en(lang) ? "Creator insights" : "Creator-Insights");

  const profileTiles: Tile[] = [];
  const followers = num(profile?.followers);
  const media = num(profile?.media_count);
  if (followers !== null) profileTiles.push({ key: "followers", label: en(lang) ? "Followers" : "Follower", value: followers, display: countText(followers, lang), sub: en(lang) ? "According to the Instagram profile" : "Laut Instagram-Profil" });
  if (media !== null) profileTiles.push({ key: "media_count", label: en(lang) ? "Posts" : "Beiträge", value: media, display: countText(media, lang), sub: en(lang) ? "Published in total" : "Veröffentlicht insgesamt" });

  // Zeitraumwerte: nur freigegebene Blöcke (die Datenbank filtert schon; hier noch einmal, falls sie mehr schickt)
  const totals = isRecord(r.period_totals) ? r.period_totals : {};
  const tiles: Tile[] = [];
  const breakdowns: Breakdown[] = [];
  const totalsWithValue = new Set<string>();
  for (const key of BLOCK_ORDER) {
    if (!has(key) || !(key in totals)) continue;
    const raw = totals[key];
    const v = num(raw);
    if (v !== null && (TILE_KEYS as readonly string[]).includes(key)) {
      const display = key === "follower_split" && v >= 0 && v <= 1 ? shareText(v, lang) : countText(v, lang);
      tiles.push({ key, label: key === "follower_split" ? (en(lang) ? "Share of followers" : "Anteil Follower") : blockLabel(key, lang), value: v, display, sub: tileSub(key, period, lang) });
      totalsWithValue.add(key);
    } else if (v !== null) {
      tiles.push({ key, label: blockLabel(key, lang), value: v, display: countText(v, lang), sub: tileSub(key, period, lang) });
      totalsWithValue.add(key);
    } else if (isRecord(raw)) {
      const b = breakdownOf(key, raw, lang);
      if (b) {
        breakdowns.push(b);
        totalsWithValue.add(key);
      }
    }
  }

  // 0340: Vergleich zum Vorzeitraum (nur 7 und 30 Tage, nur was die Tageswerte hergeben) und Engagement-Rate
  const cmp = (period === "7d" || period === "30d") && isRecord(r.compare) ? r.compare : {};
  for (const tile of tiles) if (cmp[tile.key] !== undefined && (COMPARE_KEYS as readonly string[]).includes(tile.key)) tile.compare = compareText(cmp[tile.key], lang, tile.value);
  const reach = has("reach") ? num(totals.reach) : null;
  const inter = has("interactions") ? num(totals.interactions) : null;
  if (reach !== null && reach > 0 && inter !== null) {
    const lower = periodOf(lang)[totalsPeriod(period)]?.lower ?? period;
    const rate = inter / reach;
    const at = tiles.findIndex((x) => x.key === "interactions");
    tiles.splice(at >= 0 ? at + 1 : tiles.length, 0, {
      key: "engagement_rate", label: en(lang) ? "Engagement rate" : "Engagement-Rate", value: rate, display: shareText(rate, lang),
      sub: en(lang) ? `Interactions ÷ accounts reached · ${lower}` : `Interaktionen ÷ erreichte Konten · ${lower}`,
    });
  }

  const audience = audienceBlocks(r.audience, blocks, lang);
  const stories = has("story_items") ? storiesView(r.stories, lang, nowMs) : null;
  const monthly = period === "12m" || period === "all" ? monthlyView(r.monthly, blocks, lang) : null;
  const posts = has("top_posts") ? postsView(r.top_posts, lang, mediaUrl) : null;

  const shown = new Set<string>([...totalsWithValue, ...audience.map((a) => a.block)]);
  if (profile) shown.add("profile");
  if (posts) shown.add("top_posts");
  if (stories) shown.add("story_items");
  for (const c of monthly?.columns ?? []) shown.add(MONTHLY.find((m) => m.key === c.key)!.block);
  const ordered = BLOCK_ORDER.filter(has);
  const sharedLabels = ordered.map((b) => blockLabel(b, lang));
  const missingLabels = ordered.filter((b) => !shown.has(b)).map((b) => blockLabel(b, lang));

  const subline = ["Instagram", profile ? `@${profile.username}` : null, periodLabel(period, lang)].filter(Boolean).join(" · ");
  return {
    email, agency: typeof r.agency === "string" ? r.agency : null, expiresAt: typeof r.expires_at === "string" ? r.expires_at : null,
    asOf: typeof r.as_of === "string" ? r.as_of : null, blocks: ordered, period, periodLabel: periodLabel(period, lang), title, initials: profile ? initials(name ?? profile.username) || "IG" : null, subline,
    username: profile ? profile.username : null, picture: profile ? mediaUrl(profile.picture) : null,
    trend: period === "12m" || period === "all" ? null : trendOf(r, has, period, lang),
    profileTiles, tiles, breakdowns, audience, monthly, posts, stories, audienceNote: audienceNote(r.audience_timeframe, lang), sharedLabels, missingLabels,
    empty: !profileTiles.length && !tiles.length && !breakdowns.length && !audience.length && !monthly && !posts && !stories && !profile,
    lang,
  };
}

// ── Ergebnis eines Öffnungsversuchs ───────────────────────────────────────────────────────────────────

export type ShareOutcome =
  | { kind: "open"; view: InsightsView; raw?: ShareResponse }
  | { kind: "wrong"; remaining: number }
  | { kind: "revoked" }
  /** 0425 (S-7): Vertrag der Agentur beendet */
  | { kind: "ended" }
  | { kind: "expired" }
  | { kind: "locked" }
  | { kind: "invalid" }
  | { kind: "failed" };

/** Tokens erzeugt die Datenbank als 64 Hex-Zeichen; ig_share_open verlangt mindestens 32. */
export function tokenLooksValid(token: string): boolean {
  return /^[A-Za-z0-9_-]{32,200}$/.test(token);
}
export const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function outcomeFromResponse(r: unknown, email: string): ShareOutcome {
  if (!isRecord(r)) return { kind: "failed" };
  const o = r as unknown as ShareResponse;
  if (o.ok === true) return { kind: "open", view: buildView(o, email), raw: o };
  const remaining = num(o.remaining) ?? 0;
  return remaining <= 0 ? { kind: "locked" } : { kind: "wrong", remaining };
}
/** Abgelehnt mit Grund (0023: share:revoked, share:expired, share:locked; „Ungültiger Link.“ ohne Hinweis). */
export function outcomeFromError(e: unknown): ShareOutcome {
  const hint = e instanceof ApiError ? e.hint : isRecord(e) && typeof e.hint === "string" ? e.hint : undefined;
  if (hint === "link:ended") return { kind: "ended" };
  if (hint === "share:revoked") return { kind: "revoked" };
  if (hint === "share:expired") return { kind: "expired" };
  if (hint === "share:locked") return { kind: "locked" };
  const message = e instanceof Error ? e.message : String(e);
  if (/Ungültiger Link/i.test(message)) return { kind: "invalid" };
  if (/zurückgezogen/i.test(message)) return { kind: "revoked" };
  if (/abgelaufen/i.test(message)) return { kind: "expired" };
  if (/gesperrt|Fehlversuche/i.test(message)) return { kind: "locked" };
  return { kind: "failed" };
}
